import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsClipboardPlus, BsListUl, BsTelephone } from 'react-icons/bs';

import AddToContactListModal from '#contactListFeature/components/AddToContactListModal/AddToContactListModal';
import ActionScopeButton from '#globalComponents/Table/actions/ActionScopeButton';

import { usePowerDialerStore } from '../../../stores/powerDialerStore';

import BulkAddActivityModal from './BulkAddActivityModal';
import { mapDealToDialerQueue } from './mapDealToDialerQueue';
import { withPipelineScope } from './withPipelineScope';

// The dialer queue is built from the loaded rows, so it only works on an explicit page selection.
const getPowerCallerBlocker = ({ dialerActive, isExplicitSelection, withPhoneCount }) => {
	if (dialerActive) return 'Der PowerCaller läuft bereits';
	if (!isExplicitSelection) return 'Bitte Deals auf dieser Seite auswählen';
	if (withPhoneCount === 0) return 'Keiner der ausgewählten Deals hat eine Telefonnummer';
	return null;
};

/**
 * Buttons for the deal table's actions slot. They run on the selection, or on every deal of the
 * pipeline matching the filters.
 */
const DealScopeActions = ({ pipelineId, selection, pageDeals, stageMap, aircallConnected }) => {
	const [openModal, setOpenModal] = useState(null);
	const close = () => setOpenModal(null);

	const dialerActive = usePowerDialerStore((s) => s.active);
	const startDialer = usePowerDialerStore((s) => s.start);

	const { selectedIds, actionCount, actionTarget, isExplicitSelection, toActionScope, clear } = selection;

	const selectedWithPhone = isExplicitSelection
		? pageDeals.filter((d) => selectedIds.includes(d.id) && (d.primaryContactPhone || d.companyPhoneNumber))
		: [];
	const powerCallerBlocker = getPowerCallerBlocker({
		dialerActive,
		isExplicitSelection,
		withPhoneCount: selectedWithPhone.length,
	});

	const handlePowerCaller = () => {
		const queue = selectedWithPhone.map((d) => mapDealToDialerQueue(d, stageMap)).filter(Boolean);
		if (queue.length === 0) return;
		startDialer(queue, pipelineId, 0);
		clear();
	};

	const isEmpty = actionCount === 0;

	return (
		<>
			<ActionScopeButton icon={<BsListUl />} disabled={isEmpty} onClick={() => setOpenModal('contactList')}>
				Zu Kontaktliste
			</ActionScopeButton>
			<ActionScopeButton icon={<BsClipboardPlus />} disabled={isEmpty} onClick={() => setOpenModal('activity')}>
				Aktivität anlegen
			</ActionScopeButton>
			{aircallConnected && (
				<ActionScopeButton
					icon={<BsTelephone />}
					disabled={Boolean(powerCallerBlocker)}
					title={powerCallerBlocker ?? 'Ausgewählte Deals nacheinander anrufen'}
					onClick={handlePowerCaller}
				>
					PowerCaller
				</ActionScopeButton>
			)}

			{openModal === 'contactList' && (
				<AddToContactListModal
					isOpen
					onClose={close}
					tableScope={{ table: 'crmDeals', ...withPipelineScope(toActionScope(), pipelineId) }}
					contactCount={actionCount}
					onSuccess={clear}
				/>
			)}

			{openModal === 'activity' && (
				<BulkAddActivityModal
					isOpen
					onClose={close}
					targetType="deals"
					scope={withPipelineScope(toActionScope('dealIds'), pipelineId)}
					count={actionCount}
					targetLabel={actionTarget}
					onSuccess={clear}
				/>
			)}
		</>
	);
};

DealScopeActions.propTypes = {
	pipelineId: PropTypes.string.isRequired,
	/** From useScopedSelection. */
	selection: PropTypes.shape({
		selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
		actionCount: PropTypes.number.isRequired,
		actionTarget: PropTypes.string.isRequired,
		isExplicitSelection: PropTypes.bool.isRequired,
		toActionScope: PropTypes.func.isRequired,
		clear: PropTypes.func.isRequired,
	}).isRequired,
	pageDeals: PropTypes.array.isRequired,
	stageMap: PropTypes.object.isRequired,
	aircallConnected: PropTypes.bool,
};

export default DealScopeActions;
